/**
 * Vesica Voice Avatar — realtime client (vesicavoice10k)
 * Manual client turns (turn_detection null) + reasoning.effort none — phone-safe.
 * Browser → POST session_endpoint → ephemeral token → wss://api.x.ai/v1/realtime
 * No API key in this file.
 */
(function () {
  "use strict";

  const SAMPLE_RATE = 24000;
  const WS_URL = "wss://api.x.ai/v1/realtime?model=grok-voice-latest";
  const BUILD = "vesicavoice10k";

  function $(root, sel) {
    return root.querySelector(sel);
  }

  function float32ToBase64PCM16(float32Array) {
    const pcm16 = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    const bytes = new Uint8Array(pcm16.buffer);
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }

  function base64PCM16ToFloat32(base64String) {
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const pcm16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      float32[i] = pcm16[i] / 32768.0;
    }
    return float32;
  }

  function rmsFromFloat32(samples) {
    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
      sum += samples[i] * samples[i];
    }
    return Math.sqrt(sum / Math.max(1, samples.length));
  }

  function init(root) {
    if (!root || root.dataset.vvReady === "1") return;
    root.dataset.vvReady = "1";

    const sessionEndpoint = root.getAttribute("data-session-endpoint") || "";
    const face = $(root, ".vv-face");
    const mouth = $(root, ".vv-mouth");
    const statusEl = $(root, ".vv-status");
    const transcriptEl = $(root, ".vv-transcript");
    const voiceNameEl = $(root, "[data-vv-voice]");
    const btnStart = $(root, "[data-vv-start]");
    const btnStop = $(root, "[data-vv-stop]");
    const btnMute = $(root, "[data-vv-mute]");

    let ws = null;
    let mediaStream = null;
    let audioContext = null;
    let processor = null;
    let sourceNode = null;
    let muted = false;
    let running = false;
    let nextPlayTime = 0;
    let blinkTimer = null;
    let instructions = "";
    let voice = "ara";
    let userPartial = "";
    let agentPartial = "";
    let agentSpeaking = false;
    let micGateUntil = 0;
    let lastUserFinal = "";
    let lastAgentFinal = "";
    let silentGain = null;
    let sessionExpiresAt = 0;
    let refreshTimer = null;
    let activeResponseId = null;
    let speakSafetyTimer = null;
    let playbackWatchTimer = null;
    let answerWaitTimer = null;
    let waitingForAnswer = false;
    let userSpeaking = false;
    let lastVoiceMs = 0;
    let silenceTimer = null;
    let turnBusy = false; // commit/response in flight or agent speaking

    function setStatus(text, state) {
      if (!statusEl) return;
      statusEl.textContent = text;
      statusEl.dataset.state = state || "";
    }

    function setButtons() {
      if (btnStart) btnStart.disabled = running;
      if (btnStop) btnStop.disabled = !running;
      if (btnMute) btnMute.disabled = !running;
    }

    function trimTranscript() {
      if (!transcriptEl) return;
      while (transcriptEl.children.length > 40) {
        transcriptEl.removeChild(transcriptEl.firstElementChild);
      }
    }

    function appendTranscript(role, text, replaceLast) {
      if (!transcriptEl || !text) return;
      const clean = String(text).trim();
      if (!clean) return;
      const cls = role === "user" ? "vv-line vv-line--user" : "vv-line vv-line--agent";
      const label = role === "user" ? "You" : "Vesica";
      let last = transcriptEl.lastElementChild;
      if (replaceLast && last && last.dataset.role === role && last.dataset.partial === "1") {
        last.innerHTML = "<strong>" + label + "</strong> " + escapeHtml(clean);
        transcriptEl.scrollTop = transcriptEl.scrollHeight;
        return;
      }
      // Avoid duplicate finalized lines (echo / double events)
      if (!replaceLast) {
        if (role === "user" && clean === lastUserFinal) return;
        if (role === "agent" && clean === lastAgentFinal) return;
      }
      const p = document.createElement("p");
      p.className = cls;
      p.dataset.role = role;
      p.dataset.partial = replaceLast ? "1" : "0";
      p.innerHTML = "<strong>" + label + "</strong> " + escapeHtml(clean);
      transcriptEl.appendChild(p);
      trimTranscript();
      transcriptEl.scrollTop = transcriptEl.scrollHeight;
    }

    function finalizeTranscript(role) {
      const last = transcriptEl && transcriptEl.lastElementChild;
      if (last && last.dataset.role === role) {
        last.dataset.partial = "0";
        const plain = (last.textContent || "").replace(/^(You|Vesica)\s+/, "").trim();
        if (role === "user") lastUserFinal = plain;
        if (role === "agent") lastAgentFinal = plain;
      }
    }

    function escapeHtml(s) {
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    function setMouth(scale) {
      if (!mouth) return;
      const y = Math.max(0.15, Math.min(1.6, 0.2 + scale * 8));
      mouth.style.transform = "scaleY(" + y.toFixed(3) + ")";
    }

    function scheduleBlink() {
      clearTimeout(blinkTimer);
      const wait = 3000 + Math.random() * 3000;
      blinkTimer = setTimeout(function () {
        if (face) {
          face.classList.add("vv-blink");
          setTimeout(function () {
            face.classList.remove("vv-blink");
          }, 180);
        }
        scheduleBlink();
      }, wait);
    }

    async function mintSession() {
      if (!sessionEndpoint) throw new Error("Session endpoint is not configured.");
      const res = await fetch(sessionEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (!res.ok) {
        let msg = "Could not start session (" + res.status + ").";
        try {
          const err = await res.json();
          if (err && err.error) msg = String(err.error);
        } catch (_) {}
        throw new Error(msg);
      }
      const data = await res.json();
      const token =
        (data.client_secret && data.client_secret.value) ||
        data.value ||
        "";
      if (!token) throw new Error("Session response missing token.");
      if (data.voice) voice = data.voice;
      if (data.instructions) instructions = data.instructions;
      if (voiceNameEl) voiceNameEl.textContent = voice;
      // Ephemeral tokens ~30 min (worker). Refresh early before expiry.
      var exp =
        (data.client_secret && data.client_secret.expires_at) ||
        data.expires_at ||
        0;
      sessionExpiresAt = typeof exp === "number" ? exp : 0;
      // If expires_at looks like unix seconds
      if (sessionExpiresAt > 0 && sessionExpiresAt < 1e12) {
        sessionExpiresAt = sessionExpiresAt * 1000;
      }
      if (!sessionExpiresAt) sessionExpiresAt = Date.now() + 240000;
      return token;
    }

    function connectWs(token) {
      return new Promise(function (resolve, reject) {
        const socket = new WebSocket(WS_URL, ["xai-client-secret." + token]);
        socket.addEventListener("open", function () {
          resolve(socket);
        });
        socket.addEventListener("error", function () {
          reject(new Error("WebSocket connection failed."));
        });
      });
    }

    function sendSessionUpdate(socket) {
      const base =
        instructions ||
        "You are Vesica on the ChrisObliss lounge. Warm, present, short answers.";
      const guard =
        " Keep replies short. Match the guest language. After you answer, wait for the guest.";
      const session = {
        voice: voice,
        instructions: base + guard,
        // Fast voice: do not sit in high reasoning before speaking
        reasoning: { effort: "none" },
        // Manual turns: commit is ONLY legal when turn_detection is null (xAI docs).
        // Phone server_vad often never fires speech_stopped → stuck Listening.
        turn_detection: null,
        audio: {
          input: { format: { type: "audio/pcm", rate: SAMPLE_RATE } },
          output: { format: { type: "audio/pcm", rate: SAMPLE_RATE } },
        },
      };
      socket.send(JSON.stringify({ type: "session.update", session: session }));
    }

    function clearInputBuffer() {
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
        }
      } catch (_) {}
    }

    function clearAnswerWait() {
      if (answerWaitTimer) {
        clearTimeout(answerWaitTimer);
        answerWaitTimer = null;
      }
      waitingForAnswer = false;
    }

    function clearSilenceTimer() {
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
      }
    }

    function endUserTurn(reason) {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (turnBusy || agentSpeaking || waitingForAnswer) return;
      if (!userSpeaking && reason !== "force") return;
      clearSilenceTimer();
      userSpeaking = false;
      turnBusy = true;
      waitingForAnswer = true;
      setStatus("Thinking…", "connecting");
      try {
        ws.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
        ws.send(JSON.stringify({ type: "response.create" }));
      } catch (_) {
        turnBusy = false;
        waitingForAnswer = false;
        setStatus("Could not send turn. Try again.", "error");
        return;
      }
      if (answerWaitTimer) clearTimeout(answerWaitTimer);
      answerWaitTimer = setTimeout(function () {
        if (!agentSpeaking && waitingForAnswer) {
          waitingForAnswer = false;
          turnBusy = false;
          setStatus("No answer yet. Say that again.", "error");
          try {
            clearInputBuffer();
          } catch (_) {}
        }
      }, 8000);
    }

    function noteUserVoice(level) {
      if (turnBusy || agentSpeaking || muted) return;
      var now = Date.now();
      // Speech energy gate — ignore tiny noise
      if (level >= 0.018) {
        lastVoiceMs = now;
        if (!userSpeaking) {
          userSpeaking = true;
          setStatus("Listening…", "listening");
        }
        clearSilenceTimer();
        // End turn after ~550ms of quiet after real speech
        silenceTimer = setTimeout(function () {
          if (Date.now() - lastVoiceMs >= 500) {
            endUserTurn("silence");
          }
        }, 550);
      }
    }

    function beginAgentUtterance(responseId) {
      // New spoken answer: drop any leftover queued PCM so she does not "repeat"
      if (audioContext) nextPlayTime = audioContext.currentTime;
      else nextPlayTime = 0;
      activeResponseId = responseId || activeResponseId;
      clearAnswerWait();
      clearSilenceTimer();
      userSpeaking = false;
      turnBusy = true;
      markAgentSpeaking(true);
      clearInputBuffer();
    }

    function markAgentSpeaking(on) {
      agentSpeaking = !!on;
      if (speakSafetyTimer) {
        clearTimeout(speakSafetyTimer);
        speakSafetyTimer = null;
      }
      if (playbackWatchTimer) {
        clearTimeout(playbackWatchTimer);
        playbackWatchTimer = null;
      }
      if (on) {
        clearInputBuffer();
        micGateUntil = Date.now() + 200;
        speakSafetyTimer = setTimeout(function () {
          // Safety only: force-open if something stuck
          finishAgentPlayback("safety");
        }, 10000);
      }
    }

    function finishAgentPlayback(reason) {
      if (speakSafetyTimer) {
        clearTimeout(speakSafetyTimer);
        speakSafetyTimer = null;
      }
      if (playbackWatchTimer) {
        clearTimeout(playbackWatchTimer);
        playbackWatchTimer = null;
      }
      agentSpeaking = false;
      activeResponseId = null;
      turnBusy = false;
      waitingForAnswer = false;
      userSpeaking = false;
      clearAnswerWait();
      clearSilenceTimer();
      if (audioContext) nextPlayTime = audioContext.currentTime;
      else nextPlayTime = 0;
      clearInputBuffer();
      micGateUntil = 0;
      setStatus("Listening…", "listening");
      setMouth(0.15);
    }

    function watchPlaybackEnd() {
      if (!audioContext) {
        finishAgentPlayback("no-ctx");
        return;
      }
      if (playbackWatchTimer) clearTimeout(playbackWatchTimer);
      const check = function () {
        if (!audioContext) {
          finishAgentPlayback("ctx-gone");
          return;
        }
        if (nextPlayTime - audioContext.currentTime > 20) {
          nextPlayTime = audioContext.currentTime;
        }
        const remain = nextPlayTime - audioContext.currentTime;
        if (remain <= 0.08) {
          playbackWatchTimer = setTimeout(function () {
            finishAgentPlayback("drained");
          }, 200);
          return;
        }
        playbackWatchTimer = setTimeout(check, Math.min(180, Math.max(40, remain * 400)));
      };
      check();
    }

    async function startMic() {
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });
      audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: SAMPLE_RATE,
      });
      if (audioContext.state === "suspended") await audioContext.resume();
      sourceNode = audioContext.createMediaStreamSource(mediaStream);
      processor = audioContext.createScriptProcessor(4096, 1, 1);
      processor.onaudioprocess = function (e) {
        if (!ws || ws.readyState !== WebSocket.OPEN || muted) return;
        if (agentSpeaking || turnBusy) return;
        const input = e.inputBuffer.getChannelData(0);
        let samples = input;
        if (audioContext.sampleRate !== SAMPLE_RATE) {
          samples = downsample(input, audioContext.sampleRate, SAMPLE_RATE);
        }
        noteUserVoice(rmsFromFloat32(samples));
        const b64 = float32ToBase64PCM16(samples);
        ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio: b64 }));
      };
      sourceNode.connect(processor);
      // CRITICAL: do not play mic into speakers (feedback loop on phones)
      silentGain = audioContext.createGain();
      silentGain.gain.value = 0;
      processor.connect(silentGain);
      silentGain.connect(audioContext.destination);
    }

    function downsample(buffer, fromRate, toRate) {
      if (fromRate === toRate) return buffer;
      const ratio = fromRate / toRate;
      const newLen = Math.round(buffer.length / ratio);
      const result = new Float32Array(newLen);
      for (let i = 0; i < newLen; i++) {
        result[i] = buffer[Math.floor(i * ratio)] || 0;
      }
      return result;
    }

    function playPcmBase64(b64) {
      if (!audioContext) return;
      const float32 = base64PCM16ToFloat32(b64);
      const level = rmsFromFloat32(float32);
      setMouth(level);
      const buf = audioContext.createBuffer(1, float32.length, SAMPLE_RATE);
      buf.copyToChannel(float32, 0);
      const src = audioContext.createBufferSource();
      src.buffer = buf;
      src.connect(audioContext.destination);
      const now = audioContext.currentTime;
      if (nextPlayTime < now) nextPlayTime = now;
      src.start(nextPlayTime);
      nextPlayTime += buf.duration;
      src.onended = function () {
        if (audioContext && audioContext.currentTime >= nextPlayTime - 0.05) {
          setMouth(0.15);
        }
      };
    }

    function handleServerEvent(event) {
      const type = event.type || "";

      if (type === "error" || type === "response.error") {
        const msg =
          (event.error && (event.error.message || event.error)) ||
          event.message ||
          "Something went wrong.";
        waitingForAnswer = false;
        turnBusy = false;
        userSpeaking = false;
        clearAnswerWait();
        clearSilenceTimer();
        setStatus(String(msg), "error");
        try { finishAgentPlayback("error"); } catch (_) {}
        return;
      }

      if (type === "session.updated" || type === "session.created") {
        setStatus("Listening…", "listening");
      }

      if (type === "input_audio_buffer.speech_started") {
        // Manual turns — ignore server VAD lifecycle
        userPartial = "";
      }

      if (type === "input_audio_buffer.speech_stopped") {
        // Manual turns — ignore
      }

      if (type === "input_audio_buffer.committed") {
        setStatus("Thinking…", "connecting");
      }

      if (
        type === "conversation.item.input_audio_transcription.completed" ||
        type === "conversation.item.input_audio_transcription.done"
      ) {
        const t = (event.transcript || (event.item && event.item.transcript) || "").trim();
        if (t) {
          // Ignore obvious speaker-echo of her last line
          if (lastAgentFinal && t.toLowerCase() === lastAgentFinal.toLowerCase()) {
            userPartial = "";
            clearInputBuffer();
            return;
          }
          appendTranscript("user", t, false);
          finalizeTranscript("user");
        }
        userPartial = "";
      }

      if (
        type === "conversation.item.input_audio_transcription.delta" ||
        type === "conversation.item.input_audio_transcription.updated"
      ) {
        const t = event.transcript || event.delta || event.text || "";
        if (t) {
          userPartial = type.endsWith("updated") ? t : userPartial + t;
          appendTranscript("user", userPartial, true);
        }
      }

      if (
        type === "response.output_audio_transcript.delta" ||
        type === "response.audio_transcript.delta"
      ) {
        const t = event.delta || event.transcript || "";
        if (t) {
          agentPartial += t;
          appendTranscript("agent", agentPartial, true);
        }
      }

      if (
        type === "response.output_audio_transcript.done" ||
        type === "response.audio_transcript.done"
      ) {
        const t = event.transcript || agentPartial;
        if (t) appendTranscript("agent", t, true);
        finalizeTranscript("agent");
        agentPartial = "";
      }

      if (
        type === "response.output_audio.delta" ||
        type === "response.audio.delta"
      ) {
        const rid = event.response_id || event.responseId || activeResponseId || "1";
        if (!agentSpeaking || activeResponseId !== rid) {
          beginAgentUtterance(rid);
        }
        setStatus("Speaking…", "speaking");
        const audio = event.delta || event.audio;
        if (audio) playPcmBase64(audio);
      }

      if (type === "response.created") {
        agentPartial = "";
        activeResponseId = (event.response && event.response.id) || event.response_id || String(Date.now());
        clearAnswerWait();
        waitingForAnswer = true;
        turnBusy = true;
        setStatus("Thinking…", "connecting");
        // Soft-clear mic buffer; do not lock yet (wait for real audio)
        clearInputBuffer();
      }

      if (type === "response.done" || type === "response.output_audio.done") {
        // Do NOT open mic here — audio may still be queued in the speaker
        setStatus("Speaking…", "speaking");
        watchPlaybackEnd();
      }

      if (
        type === "response.cancelled" ||
        type === "response.incomplete" ||
        type === "response.failed"
      ) {
        finishAgentPlayback("cancelled");
      }
    }


    function scheduleSessionRefresh() {
      if (refreshTimer) clearTimeout(refreshTimer);
      var ms = sessionExpiresAt - Date.now() - 45000; // refresh 45s early
      if (ms < 15000) ms = 15000;
      refreshTimer = setTimeout(function () {
        if (!running) return;
        refreshSession();
      }, ms);
    }

    async function refreshSession() {
      if (!running) return;
      try {
        setStatus("Refreshing connection…", "connecting");
        const token = await mintSession();
        // Close old socket quietly
        var old = ws;
        ws = null;
        try { if (old) old.close(); } catch (_) {}
        ws = await connectWs(token);
        sendSessionUpdate(ws);
        ws.addEventListener("message", onWsMessage);
        ws.addEventListener("close", onWsClose);
        scheduleSessionRefresh();
        setStatus("Listening…", "listening");
      } catch (err) {
        console.error("[vv] refresh", err);
        setStatus("Connection expired. Tap Start again.", "error");
        stopInternal(false);
      }
    }

    function onWsMessage(ev) {
      let data = ev.data;
      if (typeof data !== "string") return;
      try {
        handleServerEvent(JSON.parse(data));
      } catch (e) {
        console.warn("[vv] bad event", e);
      }
    }

    function onWsClose() {
      if (running) {
        setStatus("Connection closed.", "error");
        stopInternal(false);
      }
    }

    async function start() {
      if (running) return;
      if (!sessionEndpoint) {
        setStatus("Session endpoint missing in theme settings.", "error");
        return;
      }
      setStatus("Connecting…", "connecting");
      setButtons();
      try {
        const token = await mintSession();
        ws = await connectWs(token);
        running = true;
        setButtons();
        sendSessionUpdate(ws);
        ws.addEventListener("message", onWsMessage);
        ws.addEventListener("close", onWsClose);
        scheduleSessionRefresh();

        await startMic();
        setStatus("Listening…", "listening");
        scheduleBlink();
      } catch (err) {
        console.error("[vv]", err);
        setStatus(err && err.message ? err.message : "Could not start.", "error");
        stopInternal(false);
      }
    }

    function stopInternal(fromUser) {
      running = false;
      muted = false;
      clearTimeout(blinkTimer);
      if (btnMute) {
        btnMute.setAttribute("aria-pressed", "false");
        btnMute.textContent = "Mute mic";
      }
      agentSpeaking = false;
      micGateUntil = 0;
      activeResponseId = null;
      if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
      if (speakSafetyTimer) { clearTimeout(speakSafetyTimer); speakSafetyTimer = null; }
      if (playbackWatchTimer) { clearTimeout(playbackWatchTimer); playbackWatchTimer = null; }
      clearAnswerWait();
      clearSilenceTimer();
      turnBusy = false;
      waitingForAnswer = false;
      userSpeaking = false;
      try {
        if (processor) {
          processor.onaudioprocess = null;
          processor.disconnect();
        }
      } catch (_) {}
      try {
        if (silentGain) silentGain.disconnect();
      } catch (_) {}
      try {
        if (sourceNode) sourceNode.disconnect();
      } catch (_) {}
      processor = null;
      silentGain = null;
      sourceNode = null;
      if (mediaStream) {
        mediaStream.getTracks().forEach(function (t) {
          t.stop();
        });
        mediaStream = null;
      }
      if (audioContext) {
        try {
          audioContext.close();
        } catch (_) {}
        audioContext = null;
      }
      nextPlayTime = 0;
      if (ws) {
        try {
          ws.close();
        } catch (_) {}
        ws = null;
      }
      setMouth(0.15);
      setButtons();
      if (fromUser) setStatus("Stopped.", "");
    }

    function stop() {
      stopInternal(true);
    }

    function toggleMute() {
      if (!running) return;
      muted = !muted;
      if (btnMute) {
        btnMute.setAttribute("aria-pressed", muted ? "true" : "false");
        btnMute.textContent = muted ? "Unmute mic" : "Mute mic";
      }
      setStatus(muted ? "Mic muted." : "Listening…", muted ? "" : "listening");
    }

    if (btnStart) btnStart.addEventListener("click", start);
    if (btnStop) btnStop.addEventListener("click", stop);
    if (btnMute) btnMute.addEventListener("click", toggleMute);

    setButtons();
    setMouth(0.15);
    scheduleBlink();
    setStatus("Ready when you are.", "");
    root.dataset.build = BUILD;
  }

  function boot() {
    document.querySelectorAll("[data-vv-root]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
